class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.overlay = this.querySelector('[data-cart-drawer-overlay]');
    this.container = this.querySelector('[data-cart-drawer-container]');
    this.closeBtns = this.querySelectorAll('[data-cart-drawer-close]');
    this.itemsContainer = this.querySelector('[data-cart-drawer-items]');

    this.discountForm = this.querySelector('[data-cart-drawer-discount-form]');
    this.discountRemoveBtn = this.querySelector('[data-cart-drawer-discount-remove]');

    this.emptyState = this.querySelector('[data-cart-drawer-empty]');
    this.footer = this.querySelector('[data-cart-drawer-footer]');
    this.subtotalElement = this.querySelector('[data-cart-drawer-subtotal]');

    this.isOpen = false;
    this.isLoading = false;
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupKeyboardNavigation();
  }

  setupEventListeners() {

    // Close drawer events
    this.closeBtns?.forEach(btn => btn.addEventListener('click', () => this.close()));
    this.overlay?.addEventListener('click', () => this.close());
    
    // Global cart drawer open events
    document.addEventListener('cart:open-drawer', () => this.open());
    document.addEventListener('cart:close-drawer', () => this.close());
    
    // Cart update events
    document.addEventListener('cart:updated', (event) => this.handleCartUpdate(event.detail));
    
    // Quantity controls
    this.itemsContainer?.addEventListener('click', this.handleItemActions.bind(this));
    this.itemsContainer?.addEventListener('change', this.handleQuantityChange.bind(this));

    // Promotion code
    this.discountForm?.addEventListener('submit', this.handleDiscountSubmit.bind(this));
    this.discountRemoveBtn?.addEventListener('click', this.handleDiscountRemove.bind(this));

  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  async handleItemActions(event) {
    const target = event.target;

    // Find the actual button element (in case SVG or other child was clicked)
    const removeBtn = target.closest('[data-cart-remove]');
    const decreaseBtn = target.closest('[data-quantity-decrease]');
    const increaseBtn = target.closest('[data-quantity-increase]');

    if (removeBtn) {
      event.preventDefault();
      const key = removeBtn.dataset.cartRemove;
      await this.removeItem(key);
    } else if (decreaseBtn) {
      event.preventDefault();
      const quantityContainer = decreaseBtn.closest('.cart-drawer__quantity');
      const input = quantityContainer.querySelector('[data-quantity-input]');
      const currentValue = parseInt(input.value);
      if (currentValue > 1) {
        input.value = currentValue - 1;
        await this.updateQuantity(input.dataset.key, currentValue - 1);
      }
    } else if (increaseBtn) {
      event.preventDefault();
      const quantityContainer = increaseBtn.closest('.cart-drawer__quantity');
      const input = quantityContainer.querySelector('[data-quantity-input]');
      const currentValue = parseInt(input.value);
      input.value = currentValue + 1;
      await this.updateQuantity(input.dataset.key, currentValue + 1);
    }
  }

  async handleQuantityChange(event) {
    const target = event.target;
    if (target.matches('[data-quantity-input]')) {
      const key = target.dataset.key;
      const quantity = parseInt(target.value);
      if (quantity > 0) {
        await this.updateQuantity(key, quantity);
      } else {
        await this.removeItem(key);
      }
    }
  }

  async updateQuantity(key, quantity) {
    this.setLoading(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();

      this.updateCartDisplay(cart);
      this.dispatchCartUpdate(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showError('Failed to update cart');
    } finally {
      this.setLoading(false);
    }
  }

  async handleDiscountSubmit(event) {
    const target = event.target;
    const promocode = target.querySelector('input[name="discount"]').value.trim();

    event.preventDefault();

    await this.applyPromoCode(promocode);
  }

  async handleDiscountRemove(event) {
    const target = event.target;

    event.preventDefault();

    await this.removePromoCodes();
  }

  async applyPromoCode(discountCode) {
    this.setLoading(true);

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          discount: discountCode
        })
      });

      if (!response.ok) throw new Error('Failed to apply discount code');

      const cart = await response.json();
      this.updateCartDisplay(cart);
      this.dispatchCartUpdate(cart);
      
      const discountInput = this.discountForm?.querySelector('input[name="discount"]');
      if (discountInput) discountInput.value = '';
      
    } catch (error) {
      console.error('Error applying discount code:', error);
      this.showError('Failed to apply discount code');
    } finally {
      this.setLoading(false);
    }
  }

  async removePromoCodes() {
    this.setLoading(true);

    try {
      // Remove discount code by clearing the attribute
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          discount: ""
        })
      });

      if (!response.ok) throw new Error('Failed to remove discount code');

      const cart = await response.json();
      this.updateCartDisplay(cart);
      this.dispatchCartUpdate(cart);
    } catch (error) {
      console.error('Error removing discount code:', error);
      this.showError('Failed to remove discount code');
    } finally {
      this.setLoading(false);
    }
  }

  updateDiscountDisplay(cart) {
    this.refreshDiscountSection();
  }

  updateFreeShippingMessage(cart) {
    const freeShippingElement = this.querySelector('[data-cart-free-shipping-message]');
    if (!freeShippingElement) return;

    const settings = window.cartDrawerSettings;
    if (!settings || !settings.freeShippingThreshold) return;

    const currentTotal = cart.total_price;
    const threshold = settings.freeShippingThreshold;
    const amountNeeded = threshold - currentTotal;

    if (amountNeeded > 0) {
      // Still need to spend more for free shipping
      const amountFormatted = this.formatMoney(amountNeeded);
      freeShippingElement.innerHTML = settings.freeShippingMessage.replace('{{ amount }}', "<span class='font-bold'>" + amountFormatted + "</span>");
    } else {
      // Free shipping qualified
      freeShippingElement.innerHTML = "<span class='font-bold'>" + settings.freeShippingQualifiedMessage + "</span>";
    }
  }

  async refreshDiscountSection() {
    try {
      const response = await fetch(window.location.pathname + '?section_id=cart-drawer');
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDiscounts = doc.querySelector('[data-cart-drawer-discounts]');

      const currentDiscounts = this.querySelector('[data-cart-drawer-discounts]');
      if (newDiscounts && currentDiscounts) {
        currentDiscounts.innerHTML = newDiscounts.innerHTML;
        this.reattachDiscountListeners();
      }
    } catch (error) {
      console.error('Error refreshing discount section:', error);
    }
  }

  reattachDiscountListeners() {
    this.discountForm = this.querySelector('[data-cart-drawer-discount-form]');
    this.discountRemoveBtn = this.querySelector('[data-cart-drawer-discount-remove]');
    
    if (this.discountForm) {
      this.discountForm.addEventListener('submit', this.handleDiscountSubmit.bind(this));
    }
    if (this.discountRemoveBtn) {
      this.discountRemoveBtn.addEventListener('click', this.handleDiscountRemove.bind(this));
    }
  }

  async removeItem(key) {
    this.setLoading(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!response.ok) throw new Error('Failed to remove item');

      const cart = await response.json();
      this.updateCartDisplay(cart);
      this.dispatchCartUpdate(cart);
    } catch (error) {
      console.error('Error removing item:', error);
      this.showError('Failed to remove item');
    } finally {
      this.setLoading(false);
    }
  }

  async refreshCart() {
    this.setLoading(true);

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();
      this.updateCartDisplay(cart);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      this.setLoading(false);
    }
  }

  updateCartDisplay(cart) {
    if (this.subtotalElement) {
      this.subtotalElement.textContent = this.formatMoney(cart.total_price);
    }

    const isEmpty = cart.item_count === 0;
    if (this.emptyState) {
      this.emptyState.style.display = isEmpty ? 'block' : 'none';
    }
    if (this.itemsContainer) {
      this.itemsContainer.style.display = isEmpty ? 'none' : 'block';
    }
    if (this.footer) {
      this.footer.style.display = isEmpty ? 'none' : 'block';
    }

    if (!isEmpty) this.refreshCartItems();

    this.updateDiscountDisplay(cart);
    this.updateFreeShippingMessage(cart);
  }

  async refreshCartItems() {
    try {
      const response = await fetch(window.location.pathname + '?section_id=cart-drawer');
      if (!response.ok) throw new Error('Failed to refresh cart items');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newItems = doc.querySelector('[data-cart-drawer-items]');

      if (newItems && this.itemsContainer) {
        this.itemsContainer.innerHTML = newItems.innerHTML;
      }
    } catch (error) {
      console.error('Error refreshing cart items:', error);
    }
  }

  handleCartUpdate(cartData) {
    this.updateCartDisplay(cartData);
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus management
    this.closeBtn?.focus();

    // Refresh cart data when opening
    this.refreshCart();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus to trigger element if available
    const trigger = document.querySelector('[data-cart-drawer-trigger]');
    trigger?.focus();
  }

  setLoading(loading) {
    this.isLoading = loading;

    let loadingElement = this.querySelector('.cart-drawer__loading');

    if (loading && !loadingElement) {
      loadingElement = document.createElement('div');
      loadingElement.className = 'cart-drawer__loading';
      loadingElement.innerHTML = '<div class="cart-drawer__spinner"></div>';
      this.container?.appendChild(loadingElement);
    } else if (!loading && loadingElement) {
      loadingElement.remove();
    }
  }

  showError(message) {
    // Simple error handling - could be enhanced with toast notifications
    console.error(message);
  }

  formatMoney(cents) {
    const format = window.cartDrawerSettings?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = Math.round(cents / 100);
    const valueWithCommas = this.addCommasToNumber(value);
    const valueNoDecimalsWithCommas = this.addCommasToNumber(valueNoDecimals.toString());
    
    return format
      .replace('{{amount_with_comma_separator}}', valueWithCommas)
      .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimalsWithCommas)
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', valueNoDecimals);
  }

  addCommasToNumber(numberString) {
    const parts = numberString.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  dispatchCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: cart,
      bubbles: true
    }));
  }
}

// Register the custom element
customElements.define('cart-drawer', CartDrawer);

// Helper function to open cart drawer from anywhere
window.openCartDrawer = function() {
  document.dispatchEvent(new CustomEvent('cart:open-drawer'));
};

// Helper function to close cart drawer from anywhere
window.closeCartDrawer = function() {
  document.dispatchEvent(new CustomEvent('cart:close-drawer'));
};

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Add to cart form handling
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (form.matches('[action*="/cart/add"]')) {
      event.preventDefault();
      
      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) throw new Error('Failed to add to cart');
        
        const item = await response.json();
        
        // Refresh cart and open drawer
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: cart,
          bubbles: true
        }));
        
        document.dispatchEvent(new CustomEvent('cart:open-drawer'));
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }
  });
});